import { ArrowLeft, Calendar } from 'lucide-react';
import { Loading } from '@/core/ui/components/Loading';
import { formatCurrency } from '@yawi/infrastructure';
import { useJournal } from '../hooks/useJournal';
import { ReportExportButton } from '../components';

interface JournalPageProps {
  onBack: () => void;
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : d;
}

export const JournalPage = ({ onBack }: JournalPageProps) => {
  const { report, loading, exporting, params, setMonth, exportReport } = useJournal();

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Libro Diario</h2>
            <p className="text-slate-500 text-sm">Movimientos del mes en formato Debe y Haber</p>
          </div>
        </div>
        <ReportExportButton onExport={exportReport} loading={exporting} disabled={loading || !report} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
          <Calendar size={16} /> Mes
        </label>
        <input
          type="month"
          value={params.month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {loading ? (
        <Loading />
      ) : !report ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
          No hay datos para este periodo.
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 capitalize">{report.monthLabel}</p>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#8E88D6] text-white text-left">
                  <th className="px-4 py-3 font-semibold">Fecha</th>
                  <th className="px-4 py-3 font-semibold">Concepto</th>
                  <th className="px-4 py-3 font-semibold text-right">Debe</th>
                  <th className="px-4 py-3 font-semibold text-right">Haber</th>
                  <th className="px-4 py-3 font-semibold text-right">Saldo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr className="bg-slate-50">
                  <td className="px-4 py-2 text-slate-400" colSpan={2}>
                    Saldo inicial
                  </td>
                  <td />
                  <td />
                  <td className="px-4 py-2 text-right font-semibold tabular-nums">
                    {formatCurrency(report.openingBalance)}
                  </td>
                </tr>
                {report.entries.map((e) => (
                  <tr key={e.sourceId} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{formatDate(e.date)}</td>
                    <td className="px-4 py-2 text-slate-700">
                      <span className="flex items-center gap-2">
                        {e.concept}
                        {e.reference && <span className="text-xs text-slate-400">· {e.reference}</span>}
                        {!e.reconciled && (
                          <span className="text-[10px] uppercase text-amber-600 bg-amber-50 rounded px-1.5 py-0.5">
                            No conciliado
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-[#E24B4A]">
                      {e.debe > 0 ? formatCurrency(e.debe) : ''}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-[#639922]">
                      {e.haber > 0 ? formatCurrency(e.haber) : ''}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                      {formatCurrency(e.balance)}
                    </td>
                  </tr>
                ))}
                {report.entries.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                      Sin movimientos en el periodo
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-bold text-slate-800">
                  <td className="px-4 py-3" colSpan={2}>
                    Totales
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#E24B4A]">
                    {formatCurrency(report.totalDebe)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#639922]">
                    {formatCurrency(report.totalHaber)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatCurrency(report.closingBalance)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
